import React, { useEffect, useMemo, useState } from "react";
import { formatApiError } from "@/lib/api";
import { shares } from "@/api";
import { toast } from "sonner";
import {
  EnvelopeOpen, CheckCircle, XCircle, Clock, Phone, EnvelopeSimple,
  ChatCircleDots, Hourglass,
} from "@phosphor-icons/react";

const STATUS_LABEL = {
  pending: { label: "En attente", cls: "lirah-badge-warning", icon: Hourglass },
  approved: { label: "Approuvée", cls: "lirah-badge-success", icon: CheckCircle },
  rejected: { label: "Refusée", cls: "lirah-badge-danger", icon: XCircle },
};

export default function Requests({ embedded = false }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [approveTarget, setApproveTarget] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [additionalOpens, setAdditionalOpens] = useState(5);
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 10;

  const pagedRequests = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return requests.slice(start, start + PAGE_SIZE);
  }, [requests, page]);

  const totalPages = Math.max(
    1,
    Math.ceil(requests.length / PAGE_SIZE)
  );
  const rootClass = embedded
    ? "mt-6 mb-6"
    : "p-8 max-w-[1400px]";

  const headerClass = embedded
    ? "flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-4 px-1"
    : "flex items-end justify-between mb-6";


  const load = async () => {
    setLoading(true);
    try {
      const data = await shares.listRequests(filter);
      setRequests(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async () => {
    try {
      await shares.approveRequest(approveTarget.id, {
        extend_days: Number(extendDays),
        additional_opens: Number(additionalOpens),
      });
      toast.success("Lien réouvert");
      setApproveTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reject = async (r) => {
    if (!window.confirm("Refuser cette demande ?")) return;
    try {
      await shares.rejectRequest(r.id);
      toast.success("Demande refusée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div
      id={embedded ? "share-reopen-requests" : undefined}
      className={rootClass}
      data-testid={embedded ? "requests-panel" : "requests-page"}
    >
      <div className={headerClass}>
        <div>
          {!embedded ? <div className="label-eyebrow">Engagement</div> : null}
          {embedded ? (
            <h2 className="font-heading text-xl font-bold tracking-tight text-[var(--do-ink)]">
              Demandes de réouverture
            </h2>
          ) : (
            <h1 className="mt-1 font-heading text-3xl font-black tracking-tight">
              Demandes de réouverture
            </h1>
          )}
          <p className="mt-1 text-sm text-[var(--do-muted)]">
            Les prospects peuvent demander un nouvel accès quand un lien expire.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {["pending", "approved", "rejected", ""].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s || "all"}`}
              className={`px-3 py-1.5 text-xs font-heading font-semibold uppercase tracking-wider border rounded-[4px] transition-colors ${filter === s ? "bg-[var(--do-ink)] text-[var(--do-on-accent)] border-[var(--do-border-strong)]" : "border-[var(--do-border)] text-[var(--do-muted)] hover:border-[var(--do-border-strong)]"}`}
            >
              {s === "" ? "Toutes" : STATUS_LABEL[s].label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="label-eyebrow lirah-pulse">Chargement</div> : (
        requests.length === 0 ? (
          <div className="lirah-card p-12 text-center" data-testid="requests-empty">
            <EnvelopeOpen size={42} weight="duotone" className="text-[var(--do-muted)] mx-auto mb-3" />
            <div className="font-heading font-medium text-sm">Aucune demande</div>
            <div className="text-xs text-[var(--do-muted)] mt-1">Les nouvelles demandes apparaîtront ici en temps réel.</div>
          </div>
        ) : (
          <div className="au-card overflow-hidden" data-testid="requests-table">
            <div className="hidden md:block table-responsive table-scroll">
              <table className="w-full text-sm">
                <thead className="bg-[var(--do-accent-soft)] border-b border-[var(--do-border)]">
                  <tr className="text-left text-xs uppercase tracking-[0.12em] text-[var(--do-muted)]">
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3">Prospect</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Expiration</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
            {pagedRequests.map((r) => {
              const st = STATUS_LABEL[r.status] || STATUS_LABEL.pending;
              const StIcon = st.icon;
              return (
                <tr key={r.id} data-testid={`request-${r.id}`} className="border-b border-[var(--do-border)]">
                  <td className="px-4 py-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`lirah-badge ${st.cls}`}><StIcon size={11} weight="fill" />{st.label}</span>
                        <span className="text-[11px] text-[var(--do-muted)] font-mono-stat">
                          {new Date(r.createdAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                      <div className="font-heading font-bold text-base tracking-tight mt-2">{r.prospectName || "Prospect"}</div>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-[var(--do-muted)]">
                        {r.contactPhone && <div className="inline-flex items-center gap-1.5"><Phone size={12} /> {r.contactPhone}</div>}
                        {r.contactEmail && <div className="inline-flex items-center gap-1.5"><EnvelopeSimple size={12} /> {r.contactEmail}</div>}
                      </div>
                      {r.message && (
                        <div className="mt-3 border-l-2 border-[var(--lirah-primary)] pl-3 text-sm text-[var(--do-ink)] italic">
                          <ChatCircleDots size={12} className="inline mr-1 text-[var(--do-muted)]" />
                          « {r.message} »
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-3 text-[11px] text-[var(--do-muted)] font-mono-stat">
                        <span className="inline-flex items-center gap-1"><Clock size={11} /> Expirait le {r.share ? new Date(r.share.expiresAt).toLocaleDateString("fr-FR") : "—"}</span>
                        {r.share && <span>{r.share.openCount}/{r.share.maxOpens} ouvertures consommées</span>}
                      </div>
                    </div>
                    {r.status === "pending" && (
                      <div className="flex flex-col gap-2 shrink-0">
                        <button
                          onClick={() => { setApproveTarget(r); setExtendDays(7); setAdditionalOpens(5); }}
                          data-testid={`approve-${r.id}`}
                          className="lirah-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                        >
                          <CheckCircle size={13} weight="fill" /> Réouvrir
                        </button>
                        <button
                          onClick={() => reject(r)}
                          data-testid={`reject-${r.id}`}
                          className="lirah-btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                        >
                          <XCircle size={13} /> Refuser
                        </button>
                      </div>
                    )}
                  </td>
              </tr>
              );
            })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-[var(--do-border)] flex justify-between items-center text-xs">
                <span>
                  Page {page} / {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((v) => Math.max(1, v - 1))}
                    className="px-3 py-1 border rounded"
                  >
                    ←
                  </button>

                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
                    className="px-3 py-1 border rounded"
                  >
                    →
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {approveTarget && (
        <div className="acta-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" onClick={() => setApproveTarget(null)}>
          <div className="acta-modal-surface w-full max-w-md overflow-hidden rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="approve-modal">
            <div className="px-5 py-3 border-b border-[var(--do-border)]">
              <div className="font-heading font-bold text-sm">Réouvrir le lien</div>
              <div className="text-xs text-[var(--do-muted)] mt-0.5">{approveTarget.prospectName}</div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="label-eyebrow block mb-1.5">Prolonger de (jours)</label>
                <input type="number" min={1} value={extendDays} onChange={(e) => setExtendDays(e.target.value)} data-testid="extend-days-input"
                       className="w-full px-3 py-2.5 border border-[var(--do-border)] rounded-[4px] text-sm font-mono-stat" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Ouvertures supplémentaires</label>
                <input type="number" min={1} value={additionalOpens} onChange={(e) => setAdditionalOpens(e.target.value)} data-testid="additional-opens-input"
                       className="w-full px-3 py-2.5 border border-[var(--do-border)] rounded-[4px] text-sm font-mono-stat" />
              </div>
              <div className="text-xs text-[var(--do-muted)] bg-[var(--lirah-primary-soft)] border border-[var(--lirah-border-blue)] p-3 rounded-[4px]">
                Le même lien sera réactivé avec une nouvelle date d'expiration et un quota d'ouvertures rehaussé.
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setApproveTarget(null)} className="px-3 py-2 text-sm text-[var(--do-muted)]">Annuler</button>
                <button onClick={approve} className="lirah-btn-primary px-4 py-2 text-sm" data-testid="confirm-approve">Confirmer la réouverture</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
